import {
  QCFinding,
  QCRun,
  QCSectionProgress,
  QCSectionResult,
} from "../types/qcTypes";

/**
 * A section's recorded outcome as section-result fields, or undefined when the
 * server did not record one (older backend, or the section never finished).
 */
export const sectionOutcomeFromProgress = (
  progress: QCSectionProgress | undefined,
): Pick<QCSectionResult, "status" | "errorMessage"> | undefined => {
  if (!progress) return undefined;
  if (progress.Status === "completed") return { status: "complete" };
  if (progress.Status !== "failed") return undefined;
  return {
    status: "error",
    errorMessage: progress.ErrorMessage ?? undefined,
  };
};

/**
 * Rebuild section results from a finished run after the stream was lost.
 *
 * The server's findings replace whatever streamed in. Each section takes the
 * server's recorded outcome when there is one, so a failed section is not
 * shown as checked; without one (older backend) a section that had already
 * failed on the stream keeps that, and the rest are complete, as before.
 */
export const recoverSectionsFromRun = (
  prev: Map<number, QCSectionResult>,
  run: QCRun,
): Map<number, QCSectionResult> => {
  const findingsBySection = new Map<number, QCFinding[]>();
  for (const finding of run.findings) {
    const list = findingsBySection.get(finding.SectionIndex) ?? [];
    list.push(finding);
    findingsBySection.set(finding.SectionIndex, list);
  }
  const progressBySection = new Map<number, QCSectionProgress>(
    (run.sections ?? []).map((row) => [row.SectionIndex, row]),
  );

  const next = new Map(prev);
  const indexes = new Set<number>([
    ...Array.from(prev.keys()),
    ...Array.from(findingsBySection.keys()),
    ...Array.from(progressBySection.keys()),
  ]);
  for (const index of Array.from(indexes)) {
    const existing = prev.get(index);
    const outcome = sectionOutcomeFromProgress(progressBySection.get(index));
    const findings = findingsBySection.get(index) ?? [];
    if (!outcome && existing?.status === "error") continue;
    if (!outcome && !existing && findings.length === 0) continue;
    const progress = progressBySection.get(index);
    next.set(index, {
      sectionIndex: index,
      sectionId: existing?.sectionId ?? progress?.SectionID ?? undefined,
      sectionTitle:
        existing?.sectionTitle ||
        progress?.SectionTitle ||
        findings[0]?.SectionTitle ||
        `Section ${index + 1}`,
      sectionContent: existing?.sectionContent || "",
      findings,
      findingCount: findings.length,
      status: "complete",
      errorMessage: undefined,
      ...outcome,
    });
  }
  return next;
};
